import React, { useEffect, useMemo, useRef, useState } from "react";
import CreatableSelect from "react-select/creatable";
import makeAnimated from "react-select/animated";
import { showSuccess, showError } from "../../../utils/toastUtils";
import ProductAPI from "../../../api/inventory/ProductApi";
import AuthAPI from "../../../api/authApi";
import { handleApiValidationErrorforArray } from "../../../utils/apiErrorHandler";
import { buildVariantSku, sanitizeSku } from "../../../utils/skuUtils";

const animatedComponents = makeAnimated();

/* =========================================================
   HELPERS
   ========================================================= */

// Cartesian product, sirf un attributes ka jinki values select hain
const buildCombos = (keys, valuesMap) => {
  const activeKeys = keys.filter((k) => valuesMap[k]?.length);
  if (!activeKeys.length) return [];

  return activeKeys.reduce(
    (acc, key) =>
      acc.flatMap((combo) =>
        valuesMap[key].map((val) => ({ ...combo, [key]: val })),
      ),
    [{}],
  );
};

// Har combination ki stable ID (e.g. [["color","Blue"],["size","Small"]])
// Index ki jagah isi se variant pehchana jaata hai, taaki remove / add par data shift na ho
const comboKeyOf = (keys, attrs) =>
  JSON.stringify(
    keys
      .filter((k) => attrs?.[k] != null && attrs[k] !== "")
      .map((k) => [k, attrs[k]]),
  );

const comboNameOf = (keys, attrs) =>
  keys
    .map((k) => attrs?.[k])
    .filter(Boolean)
    .join("-");

// skuEdited = false -> SKU base SKU se auto-generate hota rahega
// skuEdited = true  -> user ne khud likha hai (ya DB se aaya hai), auto-overwrite nahi hoga
const createVariant = (attrs, name, comboKey, sku) => ({
  comboKey,
  attributes: attrs,
  name,
  costPrice: "",
  mrp: "",
  price: "",
  discount: "",
  expectedProfit: "",
  sku,
  skuEdited: false,
  quantity: 0,
  alertQuantity: 5,
  description: "",
  images: [],
});

// Pricing calculation ek hi jagah (pehle ye handleVariantChange + ek useEffect mein duplicate tha)
const recalcVariant = (v, field) => {
  const upd = { ...v };
  const mrp = parseFloat(upd.mrp);

  if (field === "discount") {
    const disc = parseFloat(upd.discount);
    if (!isNaN(mrp) && !isNaN(disc) && disc >= 0 && disc <= 100) {
      upd.price = Math.round(mrp * (1 - disc / 100));
    }
  } else if (field === "mrp" || field === "price") {
    const price = parseFloat(upd.price);
    upd.discount =
      !isNaN(mrp) && !isNaN(price) && mrp > 0 && price <= mrp
        ? parseFloat((((mrp - price) / mrp) * 100).toFixed(2))
        : "";
  }

  const price = parseFloat(upd.price);
  const cost = parseFloat(upd.costPrice);
  upd.expectedProfit =
    !isNaN(price) && !isNaN(cost) ? Math.round(price - cost) : "";

  return upd;
};

// Har keystroke par toast ki jagah inline warning
const getPriceWarning = (v) => {
  const mrp = parseFloat(v.mrp);
  const price = parseFloat(v.price);
  const cost = parseFloat(v.costPrice);
  const disc = parseFloat(v.discount);

  if (!isNaN(mrp) && !isNaN(price) && price > mrp) {
    return "Price cannot be greater than MRP";
  }
  if (!isNaN(cost) && !isNaN(price) && cost > price) {
    return "Cost price cannot exceed selling price";
  }
  if (v.discount !== "" && !isNaN(disc) && (disc < 0 || disc > 100)) {
    return "Discount must be between 0 and 100";
  }
  return "";
};

/* =========================================================
   COMPONENT
   ========================================================= */

function MultipleInventory({
  onSubmit,
  nextStep,
  prevStep,
  productId,
  slug,
  baseSku, // Step 1 ka SKU (e.g. SKU-PA-2605-9271). Na mile to sessionStorage se.
}) {
  const resolvedProductId = productId || sessionStorage.getItem("productId");
  const resolvedBaseSku = String(
    baseSku || sessionStorage.getItem("productSku") || "",
  )
    .trim()
    .toUpperCase();

  const [attributeKeys, setAttributeKeys] = useState([]);
  const [attributeValues, setAttributeValues] = useState({});
  const [variants, setVariants] = useState([]);
  const [excludedKeys, setExcludedKeys] = useState(() => new Set());
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Remove kiye gaye variant ka data yahan rakhte hain, taaki Restore par price / images wapas aa jaayein
  const removedCacheRef = useRef(new Map());

  /* ---------------- GENERATE VARIANTS ---------------- */
  // Combinations banata hai, removed wale skip karta hai,
  // jo combination pehle se hai uska bhara hua data preserve karta hai,
  // aur jinka SKU user ne edit nahi kiya unka SKU base SKU se banata hai
  useEffect(() => {
    const combos = buildCombos(attributeKeys, attributeValues);

    setVariants((prev) => {
      const prevMap = new Map(prev.map((v) => [v.comboKey, v]));

      return combos.reduce((list, attrs) => {
        const comboKey = comboKeyOf(attributeKeys, attrs);
        if (excludedKeys.has(comboKey)) return list;

        const name = comboNameOf(attributeKeys, attrs);
        const autoSku = buildVariantSku(resolvedBaseSku, attrs, attributeKeys);
        const existing =
          prevMap.get(comboKey) || removedCacheRef.current.get(comboKey);

        list.push(
          existing
            ? {
                ...existing,
                attributes: attrs,
                name,
                sku: existing.skuEdited ? existing.sku : autoSku,
              }
            : createVariant(attrs, name, comboKey, autoSku),
        );
        return list;
      }, []);
    });
  }, [attributeKeys, attributeValues, excludedKeys, resolvedBaseSku]);

  /* ---------------- REMOVED LIST (for Restore) ---------------- */
  const removedCombos = useMemo(
    () =>
      buildCombos(attributeKeys, attributeValues)
        .map((attrs) => ({
          comboKey: comboKeyOf(attributeKeys, attrs),
          name: comboNameOf(attributeKeys, attrs),
        }))
        .filter((c) => excludedKeys.has(c.comboKey)),
    [attributeKeys, attributeValues, excludedKeys],
  );

  /* ---------------- FETCH (EDIT MODE) ---------------- */
  useEffect(() => {
    const load = async () => {
      try {
        const res = await ProductAPI.fetchStep2VariantPrice(resolvedProductId);
        if (res.code !== 200) return;

        const raw = res.data?.variants || [];
        if (!raw.length) return;

        const keys = Object.keys(raw[0].attributes || {});
        const values = {};
        keys.forEach((k) => {
          values[k] = [
            ...new Set(raw.map((v) => v.attributes?.[k]).filter(Boolean)),
          ];
        });

        const fetched = raw.map((v) => {
          const attrs = v.attributes || {};
          return {
            comboKey: comboKeyOf(keys, attrs),
            attributes: attrs,
            name: v.variant_name || comboNameOf(keys, attrs),
            costPrice: v.costPrice ?? "",
            mrp: v.mrp ?? "",
            price: v.price ?? "",
            discount: v.discount ?? "",
            expectedProfit:
              v.price != null && v.costPrice != null
                ? Math.round(v.price - v.costPrice)
                : "",
            sku: v.sku ?? "",
            // DB wala SKU preserve rahega (barcode labels print ho chuke ho sakte hain).
            // Purane format wale SKU ke liye "Regenerate SKUs" button hai.
            skuEdited: Boolean(v.sku),
            quantity: v.stock ?? 0,
            alertQuantity: v.alertQuantity ?? 5,
            description: v.description ?? "",
            // key bhi set karo, warna save karte waqt purani images drop ho jaati thi
            images: (v.images || []).map((url) => ({ key: url, preview: url })),
          };
        });

        // Jo combination DB mein nahi hai (e.g. Blue-Small), wo "removed" maana jaayega
        const existingKeys = new Set(fetched.map((v) => v.comboKey));
        const excluded = new Set(
          buildCombos(keys, values)
            .map((attrs) => comboKeyOf(keys, attrs))
            .filter((k) => !existingKeys.has(k)),
        );

        setExcludedKeys(excluded);
        setAttributeKeys(keys);
        setAttributeValues(values);
        setVariants(fetched); // sabse last mein, taaki generate wala effect ise overwrite na kare
      } catch (err) {
        console.error(err);
        showError("Failed to load variants");
      }
    };

    load();
  }, [resolvedProductId]);

  /* ---------------- ATTRIBUTE HANDLERS ---------------- */
  const onKeysChange = (selected) => {
    const keys = (selected || []).map((opt) => opt.value);
    setAttributeKeys(keys);
    setAttributeValues((prev) =>
      Object.fromEntries(keys.map((k) => [k, prev[k] || []])),
    );
  };

  const onValuesChange = (key, selected) => {
    const vals = (selected || []).map((opt) => opt.value);
    setAttributeValues((prev) => ({ ...prev, [key]: vals }));
  };

  /* ---------------- REMOVE / RESTORE VARIANT ---------------- */
  const handleRemoveVariant = (comboKey) => {
    if (variants.length <= 1) {
      showError("At least one variant is required");
      return;
    }

    const variant = variants.find((v) => v.comboKey === comboKey);
    if (variant) removedCacheRef.current.set(comboKey, variant);

    setExcludedKeys((prev) => new Set(prev).add(comboKey));
    setErrors({}); // backend errors index-based hain, remove ke baad index badal jaate hain
  };

  const handleRestoreVariant = (comboKey) => {
    setExcludedKeys((prev) => {
      const next = new Set(prev);
      next.delete(comboKey);
      return next;
    });
    setErrors({});
  };

  /* ---------------- VARIANT FIELD CHANGES ---------------- */
  const updateVariant = (comboKey, updater) => {
    setVariants((vs) =>
      vs.map((v) => (v.comboKey === comboKey ? updater(v) : v)),
    );
  };

  const handleVariantChange = (comboKey, field, val) => {
    if (field === "sku") {
      // User ne SKU khud likha -> ab auto-generate isse overwrite nahi karega
      updateVariant(comboKey, (v) => ({
        ...v,
        sku: sanitizeSku(val),
        skuEdited: true,
      }));
      return;
    }
    updateVariant(comboKey, (v) => recalcVariant({ ...v, [field]: val }, field));
  };

  // Saare variants ke SKU dobara base SKU se banao (manual edits bhi reset ho jaayenge)
  const handleRegenerateSkus = () => {
    if (!resolvedBaseSku) {
      showError("Base SKU not found. Please save Step 1 first.");
      return;
    }

    setVariants((vs) =>
      vs.map((v) => ({
        ...v,
        sku: buildVariantSku(resolvedBaseSku, v.attributes, attributeKeys),
        skuEdited: false,
      })),
    );
    showSuccess("SKUs regenerated from base SKU");
  };

  const handleApplyToAll = () => {
    if (variants.length <= 1) return;
    const first = variants[0];

    // SKU jaan-boojh kar copy nahi hota, har variant ka unique rehna chahiye
    setVariants((vs) =>
      vs.map((v, idx) =>
        idx === 0
          ? v
          : {
              ...v,
              costPrice: first.costPrice,
              mrp: first.mrp,
              price: first.price,
              discount: first.discount,
              expectedProfit: first.expectedProfit,
              quantity: first.quantity,
              alertQuantity: first.alertQuantity,
              description: first.description,
            },
      ),
    );

    showSuccess("Applied to all variants successfully!");
  };

  /* ---------------- IMAGES ---------------- */
  const handleVariantImageChange = async (comboKey, files) => {
    if (!files.length) return;

    const allowed = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
    const max = 2 * 1024 * 1024;

    for (const file of files) {
      if (!allowed.includes(file.type)) {
        showError("Only JPG, PNG, JPEG, WEBP allowed");
        return;
      }
      if (file.size > max) {
        showError("Each file must be below 2MB");
        return;
      }
    }

    try {
      const payload = {
        files: files.map((file) => ({
          assetType: "product",
          name: `variant-${Date.now()}-${Math.random()
            .toString(36)
            .substring(2, 6)}.${file.type.split("/")[1]}`,
          mimeType: file.type,
          size: file.size,
        })),
      };

      const res = await AuthAPI.uploadFile(payload);
      const uploads = res.data;

      const results = await Promise.all(
        uploads.map((img, i) =>
          fetch(img.uploadUrl, {
            method: "PUT",
            headers: { "Content-Type": files[i].type },
            body: files[i],
          }),
        ),
      );

      if (results.some((r) => !r.ok)) {
        throw new Error("One or more uploads failed");
      }

      const newImages = files.map((file, i) => ({
        key: uploads[i].assetUrl,
        preview: URL.createObjectURL(file),
      }));

      // comboKey se update, taaki upload ke beech koi variant remove ho jaaye to image galat variant mein na jaaye
      updateVariant(comboKey, (v) => ({
        ...v,
        images: [...(v.images || []), ...newImages],
      }));
    } catch (err) {
      console.error(err);
      showError("Image upload failed");
    }
  };

  const handleRemoveImage = async (comboKey, image) => {
    if (!image) return;

    // Sirf abhi upload hui (unsaved) image ko turant storage se delete karo.
    // Saved image sirf list se hategi; DB "Next" dabane par update hoga.
    if (image.preview?.startsWith("blob:") && image.key) {
      try {
        await AuthAPI.deleteFile({ key: image.key });
      } catch (err) {
        console.error("Error while deleting image from server:", err);
      }
      URL.revokeObjectURL(image.preview);
    }

    updateVariant(comboKey, (v) => ({
      ...v,
      images: (v.images || []).filter((img) => img.preview !== image.preview),
    }));
  };

  /* ---------------- SUBMIT ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!variants.length) {
      showError("At least one variant is required");
      return;
    }

    const invalid = variants.find((v) => getPriceWarning(v));
    if (invalid) {
      showError(`${invalid.name}: ${getPriceWarning(invalid)}`);
      return;
    }

    // SKU = barcode / QR ki value, isliye har variant ka SKU zaroori aur unique hona chahiye
    const skus = variants.map((v) => (v.sku || "").trim());
    const missing = variants.find((v, i) => !skus[i]);
    if (missing) {
      showError(`${missing.name}: SKU is required`);
      return;
    }
    const duplicate = skus.find((s, i) => skus.indexOf(s) !== i);
    if (duplicate) {
      showError(`Duplicate SKU: ${duplicate}`);
      return;
    }

    const payload = {
      productId: resolvedProductId,
      slug,
      variants: variants.map((variant, i) => ({
        name: variant.name,
        costPrice: variant.costPrice,
        mrp: variant.mrp,
        price: variant.price,
        discount: variant.discount,
        sku: skus[i],
        quantity: variant.quantity,
        alertQuantity: variant.alertQuantity,
        description: variant.description,
        images: (variant.images || []).filter((img) => img.key).map((img) => img.key),
        attributes: variant.attributes,
      })),
    };

    setIsSubmitting(true);
    try {
      const res = await ProductAPI.updateStep2VariantPrice(resolvedProductId, payload);
      if (res.code === 200) {
        removedCacheRef.current.clear();
        showSuccess(res.message);
        onSubmit(payload);
      } else {
        showError(res.message);
      }
    } catch (err) {
      handleApiValidationErrorforArray(err, setErrors);
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ---------------- RENDER ---------------- */
  return (
    <form onSubmit={handleSubmit} className="card-body">
      <h5>Define Attributes</h5>
      <div>
        <CreatableSelect
          isMulti
          components={animatedComponents}
          placeholder="Add attributes (e.g. size, color, weight)"
          onChange={onKeysChange}
          onInputChange={(inputValue, actionMeta) => {
            if (actionMeta.action === "input-change") {
              return inputValue.replace(/\s/g, "");
            }
            return inputValue;
          }}
          value={attributeKeys.map((k) => ({ value: k, label: k }))}
        />
        <small style={{ color: "#888", marginTop: "5px", display: "block" }}>
          Note: Spaces are not allowed in attribute names. Use dash (-) or
          underscore (_) instead. Attribute order decides the SKU order (e.g.
          color, size → SKU-PA-RED-XL-2605-9271).
        </small>
      </div>

      {attributeKeys.map((key) => (
        <div key={key} className="mt-3">
          <label className="form-label">{key.toUpperCase()} values</label>
          <CreatableSelect
            isMulti
            components={animatedComponents}
            placeholder={`Add values for ${key}`}
            onChange={(opts) => onValuesChange(key, opts)}
            value={(attributeValues[key] || []).map((v) => ({
              value: v,
              label: v,
            }))}
          />
        </div>
      ))}

      {removedCombos.length > 0 && (
        <div className="border rounded p-3 mt-4 bg-light">
          <div className="fw-semibold mb-2">
            Removed combinations ({removedCombos.length})
          </div>
          <div className="d-flex flex-wrap gap-2">
            {removedCombos.map((c) => (
              <span
                key={c.comboKey}
                className="d-inline-flex align-items-center gap-2 border rounded bg-white px-2 py-1"
              >
                <span className="text-decoration-line-through text-muted">
                  {c.name}
                </span>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-decoration-none"
                  onClick={() => handleRestoreVariant(c.comboKey)}
                >
                  Restore
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {variants.length > 0 && (
        <>
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-4 mb-2">
            <h5 className="mb-0">Variants Details ({variants.length})</h5>
            <div className="d-flex align-items-center gap-2">
              {resolvedBaseSku && (
                <span className="fs--1 text-muted">
                  Base SKU: <code>{resolvedBaseSku}</code>
                </span>
              )}
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={handleRegenerateSkus}
                title="Rebuild every variant SKU from the base SKU"
              >
                <i className="ti ti-refresh me-1" />
                Regenerate SKUs
              </button>
            </div>
          </div>

          <div className="accordion" id="variantAccordion">
            {variants.map((v, idx) => {
              const warning = getPriceWarning(v);

              return (
                <div className="accordion-item" key={v.comboKey}>
                  <h2
                    className="accordion-header d-flex align-items-center"
                    id={`heading-${idx}`}
                  >
                    <button
                      className="accordion-button collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target={`#collapse-${idx}`}
                      aria-expanded="false"
                      aria-controls={`collapse-${idx}`}
                    >
                      Variant: <b className="ms-1">{v.name}</b>
                      {v.sku && <code className="ms-2 fs--1">{v.sku}</code>}
                      {warning && (
                        <span className="badge bg-danger ms-2">Check price</span>
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger mx-2 flex-shrink-0"
                      title="Remove this combination"
                      disabled={variants.length <= 1}
                      onClick={() => handleRemoveVariant(v.comboKey)}
                    >
                      <i className="ti ti-trash" /> Remove
                    </button>
                  </h2>

                  <div
                    id={`collapse-${idx}`}
                    className="accordion-collapse collapse"
                    aria-labelledby={`heading-${idx}`}
                    data-bs-parent="#variantAccordion"
                  >
                    <div className="accordion-body">
                      {idx === 0 && variants.length > 1 && (
                        <div className="row mb-3">
                          <div className="col-md-12 text-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={handleApplyToAll}
                            >
                              Apply First Variant to All
                            </button>
                          </div>
                        </div>
                      )}

                      <div className="row g-3">
                        <div className="col-md-4">
                          <label className="form-label">
                            Cost Price <span className="text-danger">*</span>
                          </label>
                          <input
                            type="number"
                            className="form-control"
                            value={v.costPrice}
                            onChange={(e) =>
                              handleVariantChange(v.comboKey, "costPrice", e.target.value)
                            }
                          />
                          {errors[idx]?.costPrice && (
                            <span className="errors text-danger">
                              {errors[idx].costPrice}
                            </span>
                          )}
                        </div>

                        <div className="col-md-4">
                          <label className="form-label">MRP</label>
                          <input
                            type="number"
                            className="form-control"
                            value={v.mrp}
                            onChange={(e) =>
                              handleVariantChange(v.comboKey, "mrp", e.target.value)
                            }
                          />
                          {errors[idx]?.mrp && (
                            <span className="errors text-danger">{errors[idx].mrp}</span>
                          )}
                        </div>

                        <div className="col-md-4">
                          <label className="form-label">Price</label>
                          <input
                            type="number"
                            className="form-control"
                            value={v.price}
                            onChange={(e) =>
                              handleVariantChange(v.comboKey, "price", e.target.value)
                            }
                          />
                          <span className="fs--1">
                            Expected Profit:{" "}
                            <span className="text-success">₹{v.expectedProfit}</span>
                          </span>
                          {warning && (
                            <small className="text-danger d-block">{warning}</small>
                          )}
                          {errors[idx]?.price && (
                            <span className="errors text-danger">{errors[idx].price}</span>
                          )}
                        </div>

                        <div className="col-md-4">
                          <label className="form-label">Discount (%)</label>
                          <input
                            type="text"
                            className="form-control"
                            value={v.discount}
                            onChange={(e) =>
                              handleVariantChange(v.comboKey, "discount", e.target.value)
                            }
                          />
                          {errors[idx]?.discount && (
                            <span className="errors text-danger">
                              {errors[idx].discount}
                            </span>
                          )}
                        </div>

                        <div className="col-md-8">
                          <label className="form-label">
                            SKU <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control font-monospace"
                            value={v.sku}
                            onChange={(e) =>
                              handleVariantChange(v.comboKey, "sku", e.target.value)
                            }
                          />
                          <small className="text-muted d-block">
                            {v.skuEdited
                              ? "Custom SKU (used for barcode / QR)"
                              : "Auto-generated from base SKU (used for barcode / QR)"}
                          </small>
                          {errors[idx]?.sku && (
                            <span className="errors text-danger">{errors[idx].sku}</span>
                          )}
                        </div>
                      </div>

                      <div className="row mt-3">
                        <div className="col-md-4">
                          <label className="form-label">Quantity</label>
                          <input
                            type="number"
                            className="form-control"
                            value={v.quantity}
                            onChange={(e) =>
                              handleVariantChange(v.comboKey, "quantity", e.target.value)
                            }
                          />
                          {errors[idx]?.quantity && (
                            <span className="errors text-danger">
                              {errors[idx].quantity}
                            </span>
                          )}
                        </div>

                        <div className="col-md-4">
                          <label className="form-label">Alert Quantity</label>
                          <input
                            type="number"
                            className="form-control"
                            value={v.alertQuantity}
                            onChange={(e) =>
                              handleVariantChange(v.comboKey, "alertQuantity", e.target.value)
                            }
                          />
                          {errors[idx]?.alertQuantity && (
                            <span className="errors text-danger">
                              {errors[idx].alertQuantity}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="row mt-3">
                        <div className="col-md-12">
                          <label className="form-label">Description</label>
                          <textarea
                            className="form-control"
                            rows="3"
                            value={v.description}
                            onChange={(e) =>
                              handleVariantChange(v.comboKey, "description", e.target.value)
                            }
                          />
                          {errors[idx]?.description && (
                            <span className="errors text-danger">
                              {errors[idx].description}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="row mt-3">
                        <div className="col-md-12">
                          <label className="form-label">Images</label>
                          <div className="add-choosen">
                            <div className="mb-3 image-upload image-upload-two">
                              <input
                                type="file"
                                multiple
                                accept="image/*"
                                onChange={(e) => {
                                  const files = Array.from(e.target.files || []);
                                  e.target.value = ""; // same file dobara select ho sake
                                  handleVariantImageChange(v.comboKey, files);
                                }}
                                style={{ display: "none" }}
                                id={`imageInput-${idx}`}
                              />
                              <label htmlFor={`imageInput-${idx}`} className="image-uploads">
                                <i className="ti ti-plus" />
                                <h4>Add Images</h4>
                              </label>
                            </div>

                            {v.images?.map((imgObj, imgIdx) => (
                              <div
                                className="phone-img position-relative me-3 mb-3"
                                key={imgObj.preview}
                                style={{ width: 120 }}
                              >
                                <img
                                  src={imgObj.preview}
                                  alt={`preview-${imgIdx}`}
                                  className="img-fluid"
                                  style={{ borderRadius: 4 }}
                                />
                                <button
                                  type="button"
                                  className="btn btn-sm btn-light position-absolute top-0 end-0"
                                  onClick={() => handleRemoveImage(v.comboKey, imgObj)}
                                  style={{ transform: "translate(50%,-50%)" }}
                                >
                                  &times;
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <div className="row mt-3">
        <div className="col-md-12">
          <button
            type="button"
            className="me-2 btn btn-secondary shadow-none"
            onClick={prevStep}
          >
            Prev
          </button>
          <button
            type="submit"
            className="btn btn-primary shadow-none"
            disabled={isSubmitting || !variants.length}
          >
            {isSubmitting ? "Saving..." : "Next"}
          </button>
        </div>
      </div>
    </form>
  );
}

export default MultipleInventory;